import type { ReactNode } from "react";

import { avatarDoJogador } from "./etnia";
import { BUSTO, MEDIDAS, arteDoAvatar, type MedidaDoSprite } from "./folha";

/**
 * O jogador desenhado, do peito para cima.
 *
 * **A arte vai como veio.** Nada é recortado por silhueta, recolorido, apagado
 * ou estampado: o componente escolhe qual avatar é aquele jogador e desenha o
 * busto dele.
 *
 * Isso é uma decisão, e vale escrever por quê. Houve uma versão que detectava o
 * pano do uniforme para trocá-lo pela cor do clube, e cada avatar novo trazia um
 * defeito diferente: sombra quente do braço virando asa branca no tronco, dobra
 * escura virando risco, fundo em degradê sendo pintado por fora do jogador. Eram
 * todos a mesma causa, que é adivinhar onde o pano acaba numa arte que não foi
 * feita para ser recortada. Houve depois o escudo estampado no peito, e ele
 * tapava a camisa e o desenho por baixo.
 *
 * O clube aparece na moldura da figurinha, que é o que figurinha de seleção
 * sempre fez: o uniforme da foto é branco e as cores ficam em volta.
 *
 * **Nada é guardado no save.** Qual avatar é aquele jogador sai do id e da
 * nacionalidade dele, pelo mesmo gerador do motor: o mesmo jogador sai igual em
 * qualquer máquina e em qualquer partida, sem um byte a mais no arquivo. É a
 * mesma ideia do mundo inteiro, que também nasce de uma semente.
 */

/** As medidas do avatar deste jogador, para quem precisa posicioná-lo. */
export function medidaDoJogador(playerId: string, nacionalidade?: string): MedidaDoSprite {
  return MEDIDAS[avatarDoJogador(playerId, nacionalidade)] as MedidaDoSprite;
}

export function AvatarDoJogador({
  playerId,
  nacionalidade,
  rosto = false,
}: {
  playerId: string;
  /** A sigla do país do jogador, que decide entre quais avatares sortear. */
  nacionalidade?: string;
  /**
   * Enquadra no rosto, para caber num círculo pequeno.
   *
   * O busto é mais alto que largo, então num círculo ele sobra em cima e em
   * baixo e o corte cai no peito. Puxando o enquadramento para o alto, o que
   * fica dentro do círculo é a cabeça, que é o que se reconhece de longe.
   */
  rosto?: boolean;
}): ReactNode {
  return (
    <img
      src={arteDoAvatar(avatarDoJogador(playerId, nacionalidade))}
      width={BUSTO.largura}
      height={BUSTO.altura}
      alt=""
      /*
       * `img` e não `svg`, agora que não há nada por cima do desenho.
       *
       * Enquanto o escudo era estampado no peito, a arte precisava de um
       * `viewBox` para os dois viverem no mesmo sistema de coordenadas. Sem a
       * estampa sobrou uma imagem, e imagem o navegador carrega sozinho, com
       * `lazy` e `decoding` que o SVG não tem.
       */
      loading="lazy"
      decoding="async"
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: rosto ? "50% 8%" : "50% 50%",
      }}
    />
  );
}
